import { Injectable } from '@angular/core';
import {
	BaseMessage,
	MessageNotification,
	MessageTypes,
	NotifiedMessages,
	PortfolioOrderDraftIsins,
	MarketMessage
} from '../../modules/messages/models/message.model';
import { Store } from '@ngrx/store';
import { MessageAction } from './message.action';
import { MessageStore } from './message.store';
import { MessageTabs } from '../../constants/message.constants';
import { UserDeviceService } from '../../modules/shared/services/user-device.service';
import { MobileRoutes, SharedRoutes } from '../../constants/routing.constant';
import { map } from 'rxjs/internal/operators/map';
import { MessageService } from '../../modules/messages';
import { AccountService, DeviceTypeEnum } from '../../modules/account-activity';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { OrderModelType } from '../../constants/order.constants';
import {
	LocalStorageService,
	NotificationInput,
	NotificationService,
	NotificationType,
	SymbolService,
	UserService
} from '../../modules/shared/services';
import { Observable, Subject, first, tap } from 'rxjs';
import { AuthService } from '../../modules/authentication';

@Injectable({
	providedIn: 'root'
})
export class MessageFacade {
	//TODO: must be remove later
	messagesTapsPerType: { [key in MessageTypes]: MessageTabs | undefined } = {
		[MessageTypes.ImmediateMessage]: MessageTabs.Immediate,
		[MessageTypes.MofidMessage]: MessageTabs.Mofid,
		[MessageTypes.UserMessage]: MessageTabs.Portfolio,
		[MessageTypes.MarketMessage]: MessageTabs.Broker,
		[MessageTypes.LoginMessage]: undefined,
		[MessageTypes.CodalMessage]: undefined
	};
	//TODO: must be remove later
	messagesRoutePerTab: { [key in MessageTabs]: () => string } = {
		[MessageTabs.Immediate]: () => `${this.deviceMenuroot}/${SharedRoutes.IMMEDIATE_MESSAGE_ROUTE}`,
		[MessageTabs.Mofid]: () => `${this.deviceMenuroot}/${SharedRoutes.MOFID_MESSAGE_ROUTE}`,
		[MessageTabs.Portfolio]: () => `${this.deviceMenuroot}/${SharedRoutes.PORTFOLIO_MESSAGE_ROUTE}`,
		[MessageTabs.Broker]: () => `${this.deviceMenuroot}/${SharedRoutes.MESSAGES_ROUTE}`
	};
	notifiedLsMessagesIds = new Set<string>();
	private stockChanged = new Subject<string>();
	public messageCountObs$ = this.messageStore.userMessages$.pipe(
		map((messages: MarketMessage[]) => {
			const map = new Map<string, number>();
			messages.forEach(message => {
				message.isins?.forEach(symbolIsin => {
					const count = map.get(symbolIsin) || 0;
					map.set(symbolIsin, count + 1);
				});
			});
			return map;
		})
	);
	get messagesStorageKey(): string {
		return 'messages-' + this.accountService?.userAccountInfo?.value?.customerIsin;
	}
	private get deviceMenuroot() {
		return this.userDeviceService.getDeviceMenuRoot();
	}
	constructor(
		private store: Store,
		private messageStore: MessageStore,
		private messageService: MessageService,
		private accountService: AccountService,
		private symbolService: SymbolService,
		private localStorageService: LocalStorageService,
		private userDeviceService: UserDeviceService,
		private notificationService: NotificationService,
		private userService: UserService,
		private authService: AuthService
	) {
		this.userService.userChecked$
			.pipe(
				first(res => res),
				tap(() => this.loadNotifiedLsMessagesIdsFromStorage())
			)
			.subscribe();
	}

	loadAllMessageTypes() {
		this.loadMessages();
		this.loadMofidMessages();
	}
	loadMessages(messageType?: MessageTypes) {
		if (messageType === MessageTypes.MofidMessage) {
			this.store.dispatch(MessageAction.loadMofid({ messageType }));
		} else this.store.dispatch(MessageAction.load({ messageType }));
	}
	loadMofidMessages() {
		this.store.dispatch(MessageAction.loadMofid({}));
	}
	loadMoreMofidMessages() {
		this.store.dispatch(MessageAction.loadMoreMofid());
	}
	loadMofidMessagesImage(imageId?: string) {
		this.store.dispatch(MessageAction.loadMofidImage({ imageId }));
	}
	readMessages(messageType: MessageTypes, symbolIsin?: string) {
		this.store.dispatch(MessageAction.read({ messageType, symbolIsin }));
	}
	readMessagesByIds(ids?: string[]) {
		this.store.dispatch(MessageAction.readByIds({ ids }));
	}
	seenMessageByIsin(symbolIsin?: string) {
		this.store.dispatch(MessageAction.seenMessageByIsin({ symbolIsin }));
	}
	setMessages(messages: BaseMessage[]) {
		this.store.dispatch(MessageAction.set({ messages }));
	}
	addMessages(message: BaseMessage) {
		this.store.dispatch(MessageAction.add({ message }));
	}
	subscribeToLs() {
		this.store.dispatch(MessageAction.subscribeToLs());
	}
	deleteMessages(messageId: string) {
		this.store.dispatch(MessageAction.delete({ messageId }));
	}
	updateMessages(message: BaseMessage) {
		this.store.dispatch(MessageAction.update({ message }));
	}
	updateStockChange(value: string) {
		this.stockChanged.next(value);
	}
	getUrlBasedOnUnreadMessage(): Observable<string> {
		return this.messageStore.unreadMessageState$.pipe(
			map(res => this.messagesTapsPerType[res.messageType]),
			map(selectedTab => this.messagesRoutePerTab[selectedTab]())
		);
	}
	getMessagesHistoryForIsin(isin: string) {
		return this.messageService.getMessagesHistoryForIsin(isin);
	}
	getStockChange$() {
		return this.stockChanged.asObservable();
	}
	async handleLsNotification(message: BaseMessage) {
		const userInfo = await firstValueFrom(this.accountService.userAccountInfo$);
		if (!message) return;
		if (message.type === MessageTypes.LoginMessage && message.createDateTime > userInfo.loginDate) {
			this.notifyUserLogin(message.title);
		} else this.checkMessagesToNotify();
	}
	async checkMessagesToNotify() {
		const userImmediateMessage = await firstValueFrom(this.messageStore.unreadUserImmediateMessages$);
		userImmediateMessage.forEach((t: MarketMessage) => {
			const symbolNames = t.isins?.map(isin => this.symbolService.symbolByIsin[isin]?.symbolName) ?? [];
			let combinedSymbols = symbolNames.join(', ');
			if (combinedSymbols.length > 15) combinedSymbols = combinedSymbols?.slice(0, 30) + '...';

			this.handleMarketMessageNotification(t, 'پیام فوری سهم ' + combinedSymbols);
		});

		const immediateMessage = await firstValueFrom(
			this.messageStore.unreadImmediateMessages$.pipe(map(messages => messages?.first()))
		);
		if (immediateMessage && !this.notifiedLsMessagesIds.has(immediateMessage.id)) {
			this.showNotification({
				title: 'پیام فوری دریافت شد',
				messageType: SharedRoutes.IMMEDIATE_MESSAGE_ROUTE
			});
			this.updateNotificationState(immediateMessage.id);
		}
		const portfolioIsins = await firstValueFrom(
			this.messageStore.portfolios$.pipe(map(res => res?.map(t => t.symbolIsin)))
		);

		const mofidMessage = await firstValueFrom(
			this.messageStore.unreadMofidMessages$.pipe(map(messages => messages.first()))
		);
		if (mofidMessage && !this.notifiedLsMessagesIds.has(mofidMessage.id)) {
			this.showNotification({
				title: 'پیام مفید دریافت شد',
				messageType: SharedRoutes.MOFID_MESSAGE_ROUTE
			});
			this.updateNotificationState(mofidMessage.id);
		}

		const orders = await firstValueFrom(this.messageStore.orders$);
		const omsOrderIsins = orders.filter(t => t.orderModelType === OrderModelType.oms).map(t => t.symbolIsin);
		const draftIsins = orders.filter(t => t.orderModelType === OrderModelType.draft).map(t => t.symbolIsin);
		const portfolioMessageIsins = (await firstValueFrom(this.messageStore.unreadUserMessages$)) as MarketMessage[];

		const isins = { draftIsins, omsOrderIsins, portfolioIsins };
		for (const msg of portfolioMessageIsins) {
			const { portfolioIsins, omsOrderIsins, draftIsins } = this.filteredIsinsByMessageIsins(isins, msg.isins);
			if (portfolioIsins?.length)
				this.handleSymbolMessageNotification(msg, portfolioIsins, 'پیام ناظر سهم', 'سهم موجود در پرتفوی');
			if (omsOrderIsins?.length)
				this.handleSymbolMessageNotification(msg, omsOrderIsins, 'پیام ناظر سهم', 'سهم موجود در لیست سفارش‌ها');
			if (draftIsins?.length)
				this.handleSymbolMessageNotification(msg, draftIsins, 'پیام ناظر سهم', 'سهم موجود در لیست پیش نویس ها');
		}
	}
	async getTabBasedOnUnreadMessageAsync() {
		const unreadMessage = await firstValueFrom(this.messageStore.unreadMessageState$);
		return this.messagesTapsPerType[unreadMessage.messageType];
	}
	//#region --------------------------Private method------------------------------
	private handleSymbolMessageNotification(msg: MarketMessage, isins: string[], title: string, description: string) {
		for (const isin of isins ?? []) {
			const symbol = this.symbolService.symbolByIsin[isin];
			if (symbol && msg?.isins?.indexOf(isin) >= 0) {
				this.handleMarketMessageNotification(msg, `${title} ${symbol?.symbolName ?? ''}`, description);
			}
		}
	}
	private filteredIsinsByMessageIsins(
		isins: PortfolioOrderDraftIsins | undefined,
		messageIsins: string[] | undefined
	) {
		return {
			portfolioIsins: isins?.portfolioIsins?.filter(t => messageIsins?.includes(t)),
			omsOrderIsins: isins?.omsOrderIsins?.filter(t => messageIsins?.includes(t)),
			draftIsins: isins?.draftIsins?.filter(t => messageIsins?.includes(t))
		};
	}

	private handleMarketMessageNotification(msg: MarketMessage, title?: string, des?: string) {
		if (!this.notifiedLsMessagesIds.has(msg.id)) {
			this.showNotification({
				title,
				body: des ?? '',
				messageType: SharedRoutes.PORTFOLIO_MESSAGE_ROUTE
			});
		}
		if (!this.notifiedLsMessagesIds.has(msg.id)) {
			this.updateNotificationState(msg.id);
		}
	}
	updateNotificationState(messageId: string, deleteCount: 0 | 1 = 0) {
		if (deleteCount) this.notifiedLsMessagesIds.delete(messageId);
		else this.notifiedLsMessagesIds.add(messageId);

		this.addLsNotificationIdsToStorage(this.notifiedLsMessagesIds);
	}
	private addLsNotificationIdsToStorage(notifiedIds: Set<string>) {
		const notifiedMessages: NotifiedMessages = {
			date: new Date(),
			notifiedMessageIds: Array.from(notifiedIds)
		};
		this.localStorageService.setItem(this.messagesStorageKey, JSON.stringify(notifiedMessages));
	}
	private loadNotifiedLsMessagesIdsFromStorage() {
		const notifiedMessages: NotifiedMessages = JSON.parse(
			this.localStorageService.getItem(this.messagesStorageKey)
		);
		const curDate = new Date(new Date().setHours(0, 0, 0, 0));
		if (notifiedMessages && new Date(notifiedMessages.date) > curDate && notifiedMessages.notifiedMessageIds) {
			this.notifiedLsMessagesIds = new Set<string>(notifiedMessages.notifiedMessageIds);
		} else this.addLsNotificationIdsToStorage(this.notifiedLsMessagesIds);
	}

	getMessageOption(msg: MessageNotification) {
		if (msg.messageType == SharedRoutes.IMMEDIATE_MESSAGE_ROUTE)
			return {
				notifType: NotificationType.danger,
				url: this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.IMMEDIATE_MESSAGE_ROUTE}`
			};
		if (msg.messageType == SharedRoutes.MOFID_MESSAGE_ROUTE)
			return {
				notifType: NotificationType.globalSecondary,
				url: this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.MOFID_MESSAGE_ROUTE}`
			};
		if (msg.messageType == SharedRoutes.PORTFOLIO_MESSAGE_ROUTE)
			return {
				notifType: NotificationType.pinkRose,
				url: this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.PORTFOLIO_MESSAGE_ROUTE}`
			};
		return {
			notifType: NotificationType.info,
			url: this.getDeviceEndRootPath(msg?.isin ?? '')
		};
	}
	private getDeviceEndRootPath(isin) {
		return `${
			this.userDeviceService.deviceType == DeviceTypeEnum.Mobile
				? '/' + MobileRoutes.STOCK_DETAILS_MESSAGES_ROUTE + '/' + isin
				: '/' + SharedRoutes.PORTFOLIO_MESSAGE_ROUTE
		}`;
	}

	private showNotification(msg: MessageNotification) {
		const { notifType, url } = this.getMessageOption(msg);
		this.notificationService.alert(msg.title, notifType, false, url);
		this.notificationService.notif({
			title: msg.title,
			body: msg.body ?? '',
			defaultUrl: url
		});
	}
	private notifyUserLogin(val: string) {
		const notifyInput: NotificationInput = {
			title: 'ایزی تریدر',
			body: 'لاگین همزمان به حساب شما',
			defaultUrl: this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.ACCOUNT_ACTIVITY_ROUTE}`
		};
		if (!val.includes(this.authService.msgSenderUniqId)) {
			this.notificationService.alert(
				'لاگین همزمان به حساب شما',
				NotificationType.warning,
				false,
				this.userDeviceService.getDeviceMenuRoot() + `/${SharedRoutes.ACCOUNT_ACTIVITY_ROUTE}`
			);
			this.notificationService.notif(notifyInput);
			this.accountService.accountActivitySeen(false);
		}
	}
	//#endregion
}
